import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";

/**
 * "Local support across the North West" cross-link strip.
 *
 * Rendered on every product page so Google sees each product ↔ each city
 * relationship through a bi-directional internal link (the location pages
 * reciprocate via <LocationSolutionsBlock />, and the heading links to the
 * /locations hub). This is the single
 * highest-leverage internal-linking change for local SEO on the site.
 *
 * Kept deliberately compact — 6 pill links — so it reads as contextual
 * navigation, not a footer-style link farm.
 *
 * @param {string} [exclude] — city slug to omit (unused today; reserved for
 *   future location × product pages that shouldn't self-reference).
 */
const LOCATIONS = [
  { slug: "liverpool", label: "Liverpool" },
  { slug: "manchester", label: "Manchester" },
  { slug: "chester", label: "Chester" },
  { slug: "warrington", label: "Warrington" },
  { slug: "wirral", label: "Wirral" },
];

export default function LocationCrossLinks({ exclude, testId = "location-cross-links" }) {
  const items = LOCATIONS.filter((l) => l.slug !== exclude);

  return (
    <section className="py-10 bg-white border-y border-brand-light-grey/40" data-testid={testId}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
              <MapPin size={18} className="text-brand-aqua-dark" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-1">Local Support</p>
              <h2 className="font-heading text-lg font-bold text-brand-black">
                <Link to="/locations" className="hover:text-brand-aqua-dark transition-colors" data-testid="location-link-hub">
                  Serving North West UK businesses
                </Link>
              </h2>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {items.map((l) => (
              <Link
                key={l.slug}
                to={`/locations/${l.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-light-grey hover:border-brand-aqua/50 hover:bg-brand-aqua-light/50 text-sm font-semibold text-brand-black px-4 py-2 transition-colors"
                data-testid={`location-link-${l.slug}`}
              >
                <MapPin size={12} className="text-brand-aqua" />{l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
